
import React, { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import ProductGallery from './ProductGallery';
import type { BOMType, CreateCatalogOrder, CurrentCAD, Customer, FilterType, ProductData, ProductImage } from '../../type';
import ProductConfig from './ProductConfig';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@univdiam/ui/components/table';
import { Card, CardContent, CardHeader, CardTitle } from '@univdiam/ui/components/card';
import { Button } from '@univdiam/ui/components/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Sparkles } from 'lucide-react';
import { Input } from '@univdiam/ui/components/input';
import ProductConfigForCAD from './ProductConfigForCAD';
import { renderRichText, richTextClass } from '@univdiam/ui/lib/renderRichText';
import { cn } from '@univdiam/ui/lib/utils';
import RichTextEditor from '../RichTextEditor';


const defaultProductImages: ProductImage[] = [];

const SparkleIcon = () => (
  <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
    <Sparkles className="h-5 w-5 text-gray-600" />
  </div>
);
type ProductDetailProps = {
  productImages?: ProductImage[],
  from?: string
  BOM?: BOMType[],
  filters?: ProductData,
  filterdValue?: FilterType
  setFilterdValue?: Dispatch<SetStateAction<FilterType | null>>
  selectedProduct: FilterType,
  addCad?: unknown;
  currentCAD?: CurrentCAD,
  handleCreateOrder?: (values: CreateCatalogOrder) => void
  type?: string
  shareProductTouser?: string,
  isPending?: boolean
  userBusinessName?: string
  isAdmin?: boolean
  customerData?: Customer[],
  handleBack: () => void,
  createCADOrder?: (data: any) => void,
  cadId?: string,
  submittedOrders?: any[],
  generateShareLink?: (params: { cad_design_id: string | number, share_data: Record<string, any> }) => Promise<string | null>
  actualProducts?: any[]
  generateCatalogShareLink?: (params: { stock_id: number, share_data: Record<string, any> }) => Promise<string | null>
  onSavePricing?: (bom: any[]) => Promise<void>
  /** Opens the full product edit form (discussion view only). */
  onEditProduct?: () => void
}
const ProductDetail: React.FC<ProductDetailProps> = ({
  productImages,
  from,
  BOM,
  filters,
  filterdValue,
  setFilterdValue,
  selectedProduct,
  addCad,
  currentCAD,
  handleCreateOrder,
  type,
  shareProductTouser,
  isPending,
  userBusinessName,
  isAdmin,
  customerData,
  handleBack,
  createCADOrder,
  cadId,
  submittedOrders,
  generateShareLink,
  actualProducts,
  generateCatalogShareLink,
  onSavePricing,
  onEditProduct
}) => {
  const isValidSelection = () => {
    return (
      (!filters?.diameter?.length || filterdValue?.diameter) &&
      (!filters?.carat_weight?.length || filterdValue?.caratWeight) &&
      (!filters?.diamond_quality?.length || filterdValue?.diamondQuality) &&
      (!filters?.metal_type?.length || filterdValue?.selectedMetalId)
    );
  };

  function hasDynamicFields(obj: FilterType | null | undefined): boolean {
    return !!obj && Object.keys(obj).length > 0;
  }

  const handleCatalogCreateOrder = (id?: string) => {
    if (!isValidSelection()) {
      toast.error('Please select product configuration', { position: 'top-center' })
      return;
    }
    if (isAdmin && !id) {
      toast.error('Please select a customer', { position: 'top-center' })
      return;
    }
    if (handleCreateOrder) {
      handleCreateOrder({
        customerID: id,
        job_title: (selectedProduct as any)?.title!,
        product_type: (selectedProduct as any)?.selling_price!,
        product_name: type!,
        quantity: filterdValue?.quantity?.toString()!,
        metal_type: filterdValue?.selectedMetalId!,
        caret_weight: filterdValue?.caratWeight!,
        diamond_quality: filterdValue?.diamondQuality!,
        special_instructions: filterdValue?.specialInstructions!,
        media_attachment: productImages?.map(({ src }) => src) ?? defaultProductImages?.map(({ src }) => src),
      })
    }
  }

  const selectedProductForCAD = useMemo(() => {
    if (!filterdValue) return null;

    return currentCAD?.bom?.find((product: any) => {
      return Object.entries(filterdValue).every(([key, value]) => {
        if (!value) return true; // ignore empty filters

        // normalize product field: check in product root or inside combination
        const productValue =
          product[key] ?? product.combination?.[key] ?? null;

        return productValue === value;
      });
    });
  }, [currentCAD, filterdValue]);

  // Valid only when the picks match a real BOM row. Having fields filled is not
  // enough: a combination the CAD doesn't offer matches nothing, and the order
  // request would go out with no variant and whatever price was shown last.
  const isValidSelectionForCad = () =>
    hasDynamicFields(filterdValue) && !!selectedProductForCAD;


  const [customerPrice, setCustomerPrice] = useState('')
  const [shareProductTouserFORCAD, setShareProductTouserFORCAD] = useState('')

  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [isSavingPricing, setIsSavingPricing] = useState(false);
  const [editableBOM, setEditableBOM] = useState<any[]>([]);

  useEffect(() => {
    if (BOM) {
      setEditableBOM(JSON.parse(JSON.stringify(BOM)));
    }
  }, [BOM]);

  useEffect(() => {
    if (selectedProductForCAD?.price) {
      setCustomerPrice(selectedProductForCAD?.price)
    }
    // A link made for the previous variant must not be offered for this one.
    setShareProductTouserFORCAD('')
  }, [selectedProductForCAD])

  const [isGeneratingLink, setIsGeneratingLink] = useState(false)

  const handleGenerateShareLink = async (includePrice = true) => {
    if (!generateShareLink || !currentCAD?.id || !selectedProductForCAD || (includePrice && !customerPrice)) {
      return;
    }

    const shareData: Record<string, any> = {
      title: currentCAD?.job_title,
      ...selectedProductForCAD,
      price: customerPrice,
    };

    if (!includePrice) delete shareData.price;

    setIsGeneratingLink(true);
    setShareProductTouserFORCAD('');

    try {
      const shareUrl = await generateShareLink({
        cad_design_id: currentCAD.id,
        share_data: shareData
      });

      // On failure show a toast; never put error text where a link is
      // expected, or it gets copied and sent to the customer.
      if (shareUrl) {
        setShareProductTouserFORCAD(shareUrl);
      } else {
        toast.error('Failed to generate link', { position: 'top-center' })
      }
    } catch (err) {
      toast.error('Failed to generate link', { position: 'top-center' })
      console.error('Error generating share link:', err);
    } finally {
      setIsGeneratingLink(false);
    }
  }

  // Catalog share link generation — reset when selection changes
  const [isGeneratingCatalogLink, setIsGeneratingCatalogLink] = useState(false)
  const [catalogShareLink, setCatalogShareLink] = useState('')

  useEffect(() => {
    setCatalogShareLink('')
  }, [filterdValue])

  const handleGenerateCatalogShareLink = async (includePrice = true) => {
    if (!generateCatalogShareLink || !selectedProduct) return;

    const shareData: Record<string, any> = {
      diameter: filterdValue?.diameter,
      carat_weight: filterdValue?.caratWeight,
      diamond_quality: filterdValue?.diamondQuality,
      metal_type: filterdValue?.selectedMetalId,
      quantity: filterdValue?.quantity,
      price: (selectedProduct as any)?.price,
      title: filters?.title?.[0],
    };

    if (!includePrice) delete shareData.price;

    setIsGeneratingCatalogLink(true);
    setCatalogShareLink('');

    try {
      const shareUrl = await generateCatalogShareLink({
        stock_id: (selectedProduct as any)?.stock_id,
        share_data: shareData
      });

      if (shareUrl) {
        setCatalogShareLink(shareUrl);
      }
    } catch (err) {
      console.error('Error generating catalog share link:', err);
    } finally {
      setIsGeneratingCatalogLink(false);
    }
  }

  const [instruction, setInstrcution] = useState('')

  const [notes, setNotes] = useState('')

  const handleCreateCADOrder = () => {
    if (createCADOrder) {
      createCADOrder({
        price: customerPrice,
        bom: JSON.stringify({
          title: currentCAD?.job_title,
          ...selectedProductForCAD
        }),
        instructions: instruction,
        cad_design_id: cadId ?? currentCAD?.id
      })
    }
  }

  return (
    <div className='p-[20px]'>
      <div className='border rounded-[4px] p-3  transition-colors mb-2 md:mb-3 bg-white flex items-center gap-2'>
        <ArrowLeft size={20} strokeWidth={1} onClick={handleBack} className='cursor-pointer' />
        <p className='font-semibold'>Product View</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <div className="p-4 bg-white w-full rounded-md border border-gray-200 h-fit relative md:sticky top-7">
          <ProductGallery images={productImages ?? defaultProductImages} iFrameLink={currentCAD?.iframe ?? currentCAD?.iframe_link ?? (selectedProduct as any)?.iframe_link ?? actualProducts?.find((p: any) => p.iframe_link)?.iframe_link} />
        </div>
        {
          from === 'discussion' ?
            // dicussion part CAD
            <div className='w-full'>
              <Card className="rounded-lg border border-gray-200 w-full">
                <CardHeader>
                  <CardTitle className="text-xl font-semibold text-gray-800">
                    Bill of Materials
                  </CardTitle>
                </CardHeader>
                <CardContent className='w-full'>
                  <div className="overflow-x-auto w-full">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-100">
                          {(() => {
                            if (!BOM?.length) return null;

                            const keys = Object.keys(BOM[0] || {});
                            const orderedKeys = [
                              ...keys.filter((k) => k !== "price"), // middle
                              "price" // last
                            ];

                            return orderedKeys.map((key) => (
                              <TableHead key={key} className="capitalize" style={{ width: `${100 / orderedKeys.length}%` }}>
                                {key}
                              </TableHead>
                            ));
                          })()}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(editableBOM.length > 0 ? editableBOM : (BOM || []))?.map((bom: any, idx) => {
                          const keys = Object.keys(bom);
                          const orderedKeys = [
                            ...keys.filter((k) => k !== "price"),
                            "price"
                          ];

                          return (
                            <TableRow
                              key={bom.id || idx}
                              className={`hover:bg-gray-50 transition ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/70"
                                }`}
                            >
                              {orderedKeys.map((key) => (
                                <TableCell key={key} className="font-medium">
                                  {key === "price" ? (
                                    isEditingPrice ? (
                                      <div className="flex items-center gap-1">
                                        <span>$</span>
                                        <Input
                                          type="number"
                                          className="w-24 h-8"
                                          value={bom[key] || ''}
                                          onChange={(e) => {
                                            const newBom = [...editableBOM];
                                            newBom[idx] = { ...newBom[idx], price: e.target.value };
                                            setEditableBOM(newBom);
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      `$ ${bom[key]}`
                                    )
                                  ) : (
                                    String(bom[key])
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {currentCAD?.customFields && currentCAD.customFields.length > 0 && (
                <Card className="rounded-lg border border-gray-200 w-full mt-4">
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold text-gray-800">
                      Additional Fields
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="w-full">
                    <div className="divide-y">
                      {currentCAD.customFields.map((field) => (
                        <div key={field.id} className="grid grid-cols-[30%_70%] text-sm py-2">
                          <span className="text-muted-foreground">{field.label}</span>
                          <span className="font-medium">{field.value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {currentCAD?.instruction &&
                <Card className="rounded-lg border border-gray-200 w-full mt-4">
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold text-gray-800">
                      Additional Instructions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='w-full'>
                    <div className={cn('gap-4 text-body2 leading-relaxed', richTextClass)} dangerouslySetInnerHTML={{ __html: renderRichText(currentCAD?.instruction) }}>
                    </div>
                  </CardContent>
                </Card>
              }

              {isAdmin &&
                <div className='mt-4 bg-white rounded-md border border-gray-200 p-4'>
                  <p className='text-sm font-medium mb-2'>Notes for Customer</p>
                  <RichTextEditor value={notes} onChange={setNotes} placeholder='Add notes for customer...' />
                </div>
              }

              <div className='mt-[20px] flex flex-wrap gap-4'>
                {onEditProduct &&
                  <Button
                    variant="outline"
                    type="button"
                    className="min-w-[120px]"
                    onClick={onEditProduct}
                  >
                    Edit Product
                  </Button>
                }
                <Button
                  disabled={isPending}
                  className="min-w-[160px]"
                  onClick={() => {
                    if (addCad && typeof addCad === 'function') {
                      addCad({
                        bom: editableBOM.length > 0 ? editableBOM : BOM,
                        attachments: productImages,
                        instruction: currentCAD?.instruction,
                        iFrameLink: currentCAD?.iframe,
                        notes: notes,
                        customFields: currentCAD?.customFields
                      })
                    }
                  }
                  }>
                  {
                    isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      </>
                    ) : (
                      'Send to customer'
                    )
                  }
                </Button>
                <Button
                  variant={isEditingPrice ? "default" : "outline"}
                  disabled={isSavingPricing}
                  className="min-w-[120px]"
                  onClick={async () => {
                    if (isEditingPrice && onSavePricing) {
                      setIsSavingPricing(true);
                      try {
                        await onSavePricing(editableBOM);
                      } finally {
                        setIsSavingPricing(false);
                      }
                    }
                    setIsEditingPrice(!isEditingPrice);
                  }}
                >
                  {isSavingPricing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    </>
                  ) : (isEditingPrice ? 'Save Pricing' : 'Edit Pricing')}
                </Button>
              </div>
            </div> :

            from === 'CAD' ?
              // CAD part CAD
              <div id='data-col' className='flex flex-col gap-4 w-full'>
                <div id='title-card' className='bg-white rounded-md border border-gray-200'>
                  <header className="flex items-center space-x-4 p-4 border-b border-gray-200">
                    {/* <SparkleIcon /> */}
                    <h1 className="text-body1">Product Description</h1>
                  </header>
                  {currentCAD?.fileName && (
                    <div className="px-4 pt-4">
                      <p className="text-sm font-medium text-gray-900">{currentCAD.fileName}</p>
                    </div>
                  )}
                  <h2 className={`px-4 text-[18px] ${currentCAD?.fileName ? 'pt-3' : 'pt-4'}`}>{currentCAD?.job_title}</h2>
                  <p className="px-4 pb-4 text-[18px] mt-6">{isValidSelectionForCad() ? `$ ${Number(selectedProductForCAD?.price).toLocaleString('en-US')}` : '-'}</p>
                </div>

                {currentCAD?.notes &&
                  <Card className="rounded-md border border-blue-200 w-full">
                    <CardHeader>
                      <CardTitle className="font-body1 text-gray-800">
                        Admin Notes
                      </CardTitle>
                    </CardHeader>
                    <CardContent className='w-full'>
                      <div className={cn('gap-4 text-body2 leading-relaxed', richTextClass)} dangerouslySetInnerHTML={{ __html: renderRichText(currentCAD?.notes) }}>
                      </div>
                    </CardContent>
                  </Card>
                }

                {currentCAD?.customFields && currentCAD.customFields.length > 0 &&
                  <Card className="rounded-md border border-gray-200 w-full">
                    <CardHeader>
                      <CardTitle className="font-body1 text-gray-800">
                        Additional Fields
                      </CardTitle>
                    </CardHeader>
                    <CardContent className='w-full'>
                      <div className="divide-y">
                        {currentCAD.customFields.map((field) => (
                          <div key={field.id} className="grid grid-cols-[40%_60%] text-sm py-2">
                            <span className="text-muted-foreground">{field.label}</span>
                            <span className="font-medium">{field.value}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                }

                <ProductConfigForCAD filters={filters!} filterdValue={filterdValue} setFilterdValue={setFilterdValue} isCad={true} isValidSelection={isValidSelectionForCad as any} currentCAD={currentCAD} shareProductTouser={shareProductTouserFORCAD as any} isAdmin={isAdmin} customerPrice={customerPrice} setCustomerPrice={setCustomerPrice} createCADOrder={handleCreateCADOrder} instruction={instruction} setInstrcution={setInstrcution} submittedOrders={submittedOrders} onGenerateLink={handleGenerateShareLink} isGeneratingLink={isGeneratingLink} selectedProductForCAD={selectedProductForCAD} />
              </div>
              :
              // FOR CATALOG
              <div id='data-col' className='flex flex-col gap-4 w-full'>
                <div id='title-card' className='bg-white rounded-md border border-gray-200'>
                  <header className="flex items-center space-x-4 p-4 border-b border-gray-200">
                    {/* <SparkleIcon /> */}
                    <h1 className="text-body1">Product Description</h1>
                  </header>
                  <h2 className="px-4 pt-6 text-[18px]">{filters?.title?.[0]}</h2>
                  <p className="px-4 pb-4 text-[18px] mt-6">{isValidSelection() ? `$ ${(Number((selectedProduct as any)?.price || 0) * Number(filterdValue?.quantity || 1)).toLocaleString('en-US')}` : '-'}</p>
                </div>
                <ProductConfig filters={filters!} filterdValue={filterdValue!} setFilterdValue={setFilterdValue!} handleCatalogCreateOrder={handleCatalogCreateOrder} isValidSelection={isValidSelection} isPending={isPending} isAdmin={isAdmin} customerData={customerData} selectedProduct={selectedProduct} actualProducts={actualProducts} onGenerateShareLink={handleGenerateCatalogShareLink} isGeneratingLink={isGeneratingCatalogLink} generatedShareLink={catalogShareLink} />
              </div>
        }


      </div>
    </div >
  );
};

export default ProductDetail;